import { create } from 'zustand';
import type { UserInfo, LoginParams } from '@/types/auth';
import type { Profile } from '@/types/profile';
import * as authService from '@/services/auth.service';
import { profileService } from '@/services/profile.service';
import { debugLogger } from '@/utils/debugLogger';

interface AuthState {
  // State
  accessToken: string | null;
  refreshToken: string | null;
  expiresIn: number;
  userInfo: UserInfo | null;
  roles: string[];
  permissions: string[];
  _hasHydrated: boolean;

  // Actions
  setToken: (token: string, expiresIn: number, refreshToken: string) => void;
  setUserInfo: (userInfo: UserInfo) => void;
  setRoles: (roles: string[]) => void;
  login: (params: LoginParams) => Promise<void>;
  logout: () => Promise<void>;
  refreshTokenAction: () => Promise<void>;
  getUserInfo: () => Promise<void>;
  resetState: () => void;
  hydrate: () => void;
}

// Initial state factory with hydration from localStorage
const createInitialState = () => {
  if (typeof window === 'undefined') {
    return {
      accessToken: null,
      refreshToken: null,
      expiresIn: 0,
      userInfo: null,
      roles: [],
      permissions: [],
      _hasHydrated: true,
    };
  }

  // Hydrate from localStorage
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  const expiresInStr = localStorage.getItem('expires_in');
  const userInfoStr = localStorage.getItem('user_info');

  let userInfo: UserInfo | null = null;
  if (userInfoStr) {
    try {
      userInfo = JSON.parse(userInfoStr);
      debugLogger.log('💧 [Hydrate] Restored userInfo:', userInfo?.username);
    } catch (e) {
      debugLogger.log('💧 [Hydrate] Failed to parse user_info');
    }
  }

  const state = {
    accessToken: accessToken || null,
    refreshToken: refreshToken || null,
    expiresIn: expiresInStr ? parseInt(expiresInStr, 10) : 0,
    userInfo,
    roles: [],
    permissions: [],
    _hasHydrated: true,
  };

  debugLogger.log(`💧 [Hydrate] Initial state: token=${!!state.accessToken}, user=${!!state.userInfo}`);
  return state;
};

export const useAuthStore = create<AuthState>()((set, get) => ({
  // Initialize with hydrated state
  ...createInitialState(),

      // Set token
      setToken: (token, expiresIn, refreshToken) => {
        const timestamp = new Date().toISOString();
        console.log('🔐🔐🔐 [setToken] CALLED!');
        console.log('🔐 Stack trace:', new Error().stack);
        console.log('🔐 Parameters:', {
          tokenLength: token?.length,
          expiresIn,
          refreshTokenLength: refreshToken?.length
        });

        // 写入调试日志到localStorage（不会被页面跳转清除）
        try {
          const debugLog = JSON.parse(localStorage.getItem('debug_log') || '[]');
          debugLog.push({
            timestamp,
            action: 'setToken',
            tokenLength: token?.length,
            expiresIn,
            refreshTokenLength: refreshToken?.length
          });
          localStorage.setItem('debug_log', JSON.stringify(debugLog.slice(-20))); // 只保留最近20条
        } catch (e) {
          console.error('Failed to write debug log', e);
        }

        // Check localStorage BEFORE writing
        const beforeToken = localStorage.getItem('access_token');
        console.log('🔐 BEFORE write - localStorage access_token:', beforeToken ? `EXISTS(${beforeToken.length})` : 'NULL');

        set({
          accessToken: token,
          expiresIn,
          refreshToken,
        });

        console.log('🔐 Zustand state updated');

        // Verify state was updated
        const currentState = get();
        console.log('🔐 State verification:', {
          hasAccessToken: !!currentState.accessToken,
          hasRefreshToken: !!currentState.refreshToken
        });

        // Save to localStorage for axios interceptor
        if (typeof window !== 'undefined') {
          console.log('🔐 Writing to localStorage...');

          try {
            localStorage.setItem('access_token', token);
            console.log('🔐 ✅ Wrote access_token');

            localStorage.setItem('refresh_token', refreshToken);
            console.log('🔐 ✅ Wrote refresh_token');

            localStorage.setItem('expires_in', expiresIn.toString());
            console.log('🔐 ✅ Wrote expires_in');

            // IMMEDIATELY verify it was saved
            const verifyToken = localStorage.getItem('access_token');
            const verifyRefresh = localStorage.getItem('refresh_token');
            const verifyExpires = localStorage.getItem('expires_in');

            console.log('🔐 IMMEDIATE verification after write:', {
              access_token: verifyToken ? `EXISTS(${verifyToken.length})` : 'NULL ❌',
              refresh_token: verifyRefresh ? `EXISTS(${verifyRefresh.length})` : 'NULL ❌',
              expires_in: verifyExpires || 'NULL ❌'
            });

            if (!verifyToken || !verifyRefresh) {
              console.error('🚨🚨🚨 [setToken] CRITICAL: localStorage write FAILED!');
              console.error('🚨 Token written but not found in localStorage!');

              // 记录写入失败
              try {
                const debugLog = JSON.parse(localStorage.getItem('debug_log') || '[]');
                debugLog.push({
                  timestamp,
                  action: 'setToken_WRITE_FAILED',
                  verifyToken: !!verifyToken,
                  verifyRefresh: !!verifyRefresh
                });
                localStorage.setItem('debug_log', JSON.stringify(debugLog.slice(-20)));
              } catch (e) {}
            } else {
              // 记录写入成功
              try {
                const debugLog = JSON.parse(localStorage.getItem('debug_log') || '[]');
                debugLog.push({
                  timestamp,
                  action: 'setToken_SUCCESS',
                  tokenLength: verifyToken.length,
                  refreshLength: verifyRefresh.length
                });
                localStorage.setItem('debug_log', JSON.stringify(debugLog.slice(-20)));
              } catch (e) {}
            }

            // Also save to cookies for Next.js middleware
            const expires = new Date();
            expires.setSeconds(expires.getSeconds() + expiresIn);
            document.cookie = `access_token=${token}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
            document.cookie = `refresh_token=${refreshToken}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
            console.log('🔐 ✅ Saved to cookies');
          } catch (error) {
            console.error('🚨🚨🚨 [setToken] EXCEPTION during localStorage write!', error);
          }
        } else {
          console.warn('🔐 ⚠️ window is undefined, cannot write to localStorage');
        }

        console.log('🔐 [setToken] COMPLETE');
      },

      // Set user info
      setUserInfo: (userInfo) => {
        console.log('👤👤👤 [setUserInfo] CALLED!');
        console.log('👤 Stack trace:', new Error().stack);
        console.log('👤 UserInfo:', userInfo);
        console.log(`👤 User type: ${userInfo?.user_type}`);
        console.log(`👤 Username: ${userInfo?.username}`);

        // Check localStorage BEFORE writing
        const beforeUserInfo = localStorage.getItem('user_info');
        console.log('👤 BEFORE write - localStorage user_info:', beforeUserInfo ? 'EXISTS' : 'NULL');

        set({ userInfo });
        console.log('👤 Zustand state updated');

        // Also save to localStorage for usePermission hook
        try {
          if (typeof window !== 'undefined' && userInfo) {
            console.log('👤 Writing to localStorage...');
            const jsonString = JSON.stringify(userInfo);
            console.log(`👤 JSON stringified, length: ${jsonString.length}`);

            localStorage.setItem('user_info', jsonString);
            console.log('👤 ✅ localStorage.setItem() completed');

            // IMMEDIATELY verify it was saved correctly
            const saved = localStorage.getItem('user_info');
            if (saved) {
              const parsed = JSON.parse(saved);
              console.log('👤 IMMEDIATE verification: user_type:', parsed.user_type);
            } else {
              console.error('🚨🚨🚨 [setUserInfo] CRITICAL: localStorage write FAILED!');
              console.error('🚨 UserInfo written but not found in localStorage!');
            }
          } else {
            console.warn('👤 ⚠️ Skipping localStorage save - window undefined or userInfo null');
          }
        } catch (error) {
          console.error('🚨🚨🚨 [setUserInfo] EXCEPTION during localStorage save!', error);
          console.error('🚨 Stack:', error instanceof Error ? error.stack : 'no stack');
        }
        console.log('👤 [setUserInfo] COMPLETE');
      },

      // Set roles
      setRoles: (roles) => {
        set({ roles });
      },

      // Login
      login: async (params) => {
        try {
          const result = await authService.login(params);

          // Save token
          get().setToken(
            result.access_token,
            result.expires_in,
            result.refresh_token
          );

          // Get user info
          await get().getUserInfo();
        } catch (error) {
          throw error;
        }
      },

      // Logout
      logout: async () => {
        const { refreshToken } = get();

        try {
          if (refreshToken) {
            await authService.logout(refreshToken);
          }
        } catch (error) {
          console.error('Logout failed:', error);
        } finally {
          get().resetState();
        }
      },

      // Refresh token
      refreshTokenAction: async () => {
        const { refreshToken } = get();

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        try {
          const result = await authService.refreshToken({
            refresh_token: refreshToken,
          });

          get().setToken(
            result.access_token,
            result.expires_in,
            refreshToken
          );
        } catch (error) {
          get().resetState();
          throw error;
        }
      },

      // Get user info
      getUserInfo: async () => {
        try {
          const profile: Profile = await profileService.getProfile();
          const userInfo: UserInfo = {
            username: profile.username,
            nickname: profile.nickname ?? profile.name,
            user_type: profile.user_type,
            id: profile.id,
            name: profile.name,
            email: profile.email,
            avatar: profile.avatar,
            company_name: profile.company_name,
            contact_person: profile.contact_person,
          };

          let roles: string[] = [];
          try {
            const rulesResponse = await authService.getSystemRules();
            roles = rulesResponse.list || [];
          } catch (error) {
            console.warn('Failed to get system rules, using empty permissions', error);
            roles = [];
          }

          get().setUserInfo(userInfo);
          get().setRoles(roles);

          // Also update permissions
          set({ permissions: roles });
        } catch (error) {
          throw error;
        }
      },

  // Manual hydration function (if needed to re-sync from localStorage)
  hydrate: () => {
    if (typeof window !== 'undefined') {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      const expiresInStr = localStorage.getItem('expires_in');
      const userInfoStr = localStorage.getItem('user_info');

      let userInfo: UserInfo | null = null;
      if (userInfoStr) {
        try {
          userInfo = JSON.parse(userInfoStr);
        } catch (e) {
          console.error('Failed to parse user_info during hydration');
        }
      }

      set({
        accessToken: accessToken || null,
        refreshToken: refreshToken || null,
        expiresIn: expiresInStr ? parseInt(expiresInStr, 10) : 0,
        userInfo,
        _hasHydrated: true,
      });

      debugLogger.log(`💧 [Manual Hydrate] Restored: token=${!!accessToken}, user=${!!userInfo}`);
    }
  },

  // Reset state
  resetState: () => {
    const timestamp = new Date().toISOString();
    const stack = new Error().stack;
    console.error('💥💥💥 [AuthStore] resetState() CALLED!');
    console.error('💥 Stack trace:', stack);

    // 记录resetState调用（写入BEFORE清空localStorage）
    try {
      const debugLog = JSON.parse(localStorage.getItem('debug_log') || '[]');
      debugLog.push({
        timestamp,
        action: 'resetState_CALLED',
        stack: stack?.split('\n').slice(0, 5).join('\n') // 只保留前5行stack
      });
      localStorage.setItem('debug_log', JSON.stringify(debugLog.slice(-20)));
    } catch (e) {}

    set({
      accessToken: null,
      refreshToken: null,
      expiresIn: 0,
      userInfo: null,
      roles: [],
      permissions: [],
    });

    // Clear localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('expires_in');
      localStorage.removeItem('user_info');
      localStorage.removeItem('auth-storage'); // Also clean up old persist storage

      // Clear cookies
      document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax';
      document.cookie = 'refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax';

      // 注意：我们不清除debug_log，这样即使跳转后也能看到日志
    }
    console.error('💥 [AuthStore] resetState() COMPLETE');
  },
}));
